import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Avatar, List, Popconfirm, Spin, Tag, Tooltip, Typography } from "antd";
import { getManagerDependingVariables } from "@devowl-wp/cookie-consent-web-client";
import { getCookieTypeLocalizationMap } from "@devowl-wp/react-cookie-banner";
import { reactNl2Br } from "@devowl-wp/react-utils";
import { useI18n } from "../../../contexts/i18n.js";
import { useListService } from "../../../contexts/listService.js";
import { useUpsell } from "../../../contexts/upsell.js";
import { ListMultilingualLanguages } from "../../common/listMultilingualLanguages.js";
const { Paragraph } = Typography;
const ListServiceRow = ({ busy, attributes: { isEssential, deleteHint, deletable, id, name, purpose, status, isEmbeddingOnlyExternalResources, uniqueName, technicalDefinitions, legalBasis, tagManagerOptInEventName, tagManagerOptOutEventName, executeCodeOptInWhenNoTagManagerConsentIsGiven, executeCodeOptOutWhenNoTagManagerConsentIsGiven, codeOptIn, codeOptOut, deleteTechnicalDefinitionsAfterOptOut, codeOnPageLoad, presetId }, avatarUrl, isUpdateAvailable, languages, languageOnClick, dragHandle })=>{
    const { __ } = useI18n();
    const { isLicensed } = useUpsell();
    const { isConsentForwarding, onEdit, onDelete, setCookiesViaManager, isGcm } = useListService();
    const cookieTypeLocalized = getCookieTypeLocalizationMap();
    const firstTechnicalDefinition = technicalDefinitions?.[0];
    const { managerLabel, serviceIsManager, features } = getManagerDependingVariables(setCookiesViaManager, {
        isGcm,
        presetId
    });
    return /*#__PURE__*/ _jsx(List.Item, {
        itemID: id.toString(),
        actions: [
            /*#__PURE__*/ _jsx("a", {
                onClick: ()=>onEdit(id),
                children: isUpdateAvailable ? __("Edit and update") : __("Edit")
            }, "edit"),
            deletable ? /*#__PURE__*/ _jsx(Popconfirm, {
                title: reactNl2Br([
                    __("Are you sure you want to delete this service?"),
                    ...deleteHint
                ].join("\n\n")),
                placement: "bottomRight",
                onConfirm: ()=>onDelete(id),
                okText: __("Delete"),
                cancelText: __("Cancel"),
                overlayStyle: {
                    maxWidth: 350
                },
                children: /*#__PURE__*/ _jsx("a", {
                    style: {
                        cursor: "pointer"
                    },
                    children: __("Delete")
                })
            }, "delete") : /*#__PURE__*/ _jsx(Tooltip, {
                placement: "topRight",
                arrow: {
                    pointAtCenter: true
                },
                overlay: __(`The "%s" service cannot be deleted.`, name),
                children: /*#__PURE__*/ _jsx("a", {
                    style: {
                        opacity: 0.5
                    },
                    children: __("Delete")
                })
            }),
            languages?.length && /*#__PURE__*/ _jsx(ListMultilingualLanguages, {
                recordId: id,
                languages: languages,
                onClick: languageOnClick
            }, "languages"),
            /*#__PURE__*/ _jsx("a", {
                children: dragHandle
            }, "drag")
        ].filter(Boolean),
        children: /*#__PURE__*/ _jsx(Spin, {
            spinning: busy,
            children: /*#__PURE__*/ _jsx(List.Item.Meta, {
                avatar: avatarUrl ? /*#__PURE__*/ _jsx(Avatar, {
                    size: "large",
                    src: avatarUrl,
                    shape: "square"
                }) : /*#__PURE__*/ _jsx(Avatar, {
                    size: "large",
                    style: {
                        backgroundColor: isEmbeddingOnlyExternalResources ? "#4ea29a" : cookieTypeLocalized[firstTechnicalDefinition.type].backgroundColor
                    },
                    children: isEmbeddingOnlyExternalResources ? __("None") : cookieTypeLocalized[firstTechnicalDefinition.type].abbr
                }),
                title: /*#__PURE__*/ _jsxs("span", {
                    children: [
                        name,
                        " ",
                        status === "draft" ? /*#__PURE__*/ _jsx(Tag, {
                            color: "orange",
                            children: __("Draft")
                        }) : status === "private" ? /*#__PURE__*/ _jsx(Tag, {
                            color: "red",
                            children: __("Disabled")
                        }) : null,
                        !!isEmbeddingOnlyExternalResources && /*#__PURE__*/ _jsx(Tag, {
                            children: __("No technical cookies")
                        }),
                        !isEssential && legalBasis === "legitimate-interest" && /*#__PURE__*/ _jsx(Tag, {
                            children: __("Legitimate interest (Opt-out)")
                        }),
                        isEssential && legalBasis === "legal-requirement" && /*#__PURE__*/ _jsx(Tag, {
                            children: __("Compliance with a legal obligation")
                        }),
                        !!presetId && /*#__PURE__*/ _jsx(Tag, {
                            children: __("Created from template")
                        }),
                        !!presetId && !avatarUrl && /*#__PURE__*/ _jsx(Tooltip, {
                            title: isLicensed ? __("There is no longer a service template for this service. Probably the service has been discontinued. Please look for alternatives!") : __("This service was created from a template. As you do not have a license activated at the moment, updates that are potentially available cannot be downloaded."),
                            children: /*#__PURE__*/ _jsx(Tag, {
                                color: "red",
                                children: isLicensed ? __("No longer supported") : __("Possibly outdated")
                            })
                        }),
                        !!isUpdateAvailable && /*#__PURE__*/ _jsx(Tooltip, {
                            title: __("The service template has been updated to provide current legal and technical information."),
                            children: /*#__PURE__*/ _jsx(Tag, {
                                color: "green",
                                children: __("Update available")
                            })
                        })
                    ]
                }),
                description: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        !!purpose && /*#__PURE__*/ _jsx(Paragraph, {
                            style: {
                                color: "inherit",
                                marginBottom: 0
                            },
                            ellipsis: {
                                rows: 3
                            },
                            children: reactNl2Br(purpose)
                        }),
                        !!(codeOptIn || codeOptOut || codeOnPageLoad) && /*#__PURE__*/ _jsxs("div", {
                            style: {
                                paddingTop: 5
                            },
                            children: [
                                __("Executes code on"),
                                ":",
                                " ",
                                !!codeOptIn && /*#__PURE__*/ _jsxs(Tag, {
                                    children: [
                                        __("Opt-in"),
                                        features.executeCodeWhenNoTagManagerConsentIsGiven && executeCodeOptInWhenNoTagManagerConsentIsGiven && /*#__PURE__*/ _jsx(Tooltip, {
                                            title: __("Only run this script if the user has not consented to use %s", managerLabel),
                                            children: /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    " ",
                                                    /*#__PURE__*/ _jsx(InfoCircleOutlined, {})
                                                ]
                                            })
                                        })
                                    ]
                                }),
                                (!!codeOptOut || deleteTechnicalDefinitionsAfterOptOut) && !isEssential && /*#__PURE__*/ _jsxs(Tag, {
                                    children: [
                                        __("Opt-out"),
                                        features.executeCodeWhenNoTagManagerConsentIsGiven !== false && executeCodeOptOutWhenNoTagManagerConsentIsGiven && /*#__PURE__*/ _jsx(Tooltip, {
                                            title: __("Only run this script if the user has not consented to use %s", managerLabel),
                                            children: /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    " ",
                                                    /*#__PURE__*/ _jsx(InfoCircleOutlined, {})
                                                ]
                                            })
                                        }),
                                        deleteTechnicalDefinitionsAfterOptOut && /*#__PURE__*/ _jsx(Tooltip, {
                                            title: __("Delete all first-party cookies after opt-out. First-party cookies are only cookies that are set by or for this domain."),
                                            children: /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    " ",
                                                    /*#__PURE__*/ _jsx(DeleteOutlined, {})
                                                ]
                                            })
                                        })
                                    ]
                                }),
                                !!codeOnPageLoad && /*#__PURE__*/ _jsx(Tag, {
                                    children: __("Page load")
                                })
                            ]
                        }),
                        !!managerLabel && /*#__PURE__*/ _jsx("div", {
                            style: {
                                paddingTop: 5
                            },
                            children: serviceIsManager ? /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    __("Opt-in script loads"),
                                    ": ",
                                    /*#__PURE__*/ _jsx(Tag, {
                                        children: managerLabel
                                    })
                                ]
                            }) : features.events && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    __("%s Events", managerLabel),
                                    ":",
                                    " ",
                                    /*#__PURE__*/ _jsxs(Tag, {
                                        children: [
                                            /*#__PURE__*/ _jsxs("strong", {
                                                children: [
                                                    __("Opt-in"),
                                                    ": "
                                                ]
                                            }),
                                            tagManagerOptInEventName || __("None")
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Tag, {
                                        children: [
                                            /*#__PURE__*/ _jsxs("strong", {
                                                children: [
                                                    __("Opt-out"),
                                                    ": "
                                                ]
                                            }),
                                            tagManagerOptOutEventName || __("None")
                                        ]
                                    })
                                ]
                            })
                        }),
                        isConsentForwarding && /*#__PURE__*/ _jsxs("div", {
                            style: {
                                paddingTop: 5
                            },
                            children: [
                                __("Consent Forwarding Unique Name"),
                                ": ",
                                /*#__PURE__*/ _jsx(Tag, {
                                    children: uniqueName
                                })
                            ]
                        })
                    ]
                })
            })
        })
    });
};
export { ListServiceRow };
